import React, {
	useEffect,
	useMemo,
	useState,
	useRef,
	useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import * as ChatActions from '../../stores/actions/chat';
import type {
	ChatMessage, ChatPost, ChatRoom,
} from '../../stores/types/chat';
import { EStatus } from '../../stores/types/chat';
import { makeId } from '../../stores/reducers/chat';
import Socket from '../../connection/socket';
import { ConnectedUser } from '../../stores/types/connection';
import { uniqueArray } from '../../utils/utils';
import * as InvitationActions from '../../stores/actions/invitations';
import ChatRoomView from './chat-room';
import ChatConvoView from './chat-convo';
import ChatLink from './chat-link';
import { CreateRoom } from '../../connection/chat';
import Icon, { ICONS, COLORS } from '../general-components/icon';
import ModeratedComments from './moderated-comments';
import { GreenroomState } from '../../stores/types/types';
import * as Utils from '../../utils/utils';
import useChat from '../../hooks/use-chat';
import * as Storage from '../../utils/local-storage';
import useLoad from '../../hooks/use-load';
import InviteBadge from './invite-badge';

interface OwnProps {
	// props that will be passed to this component
	socket: Socket | null;
	// visible
	visible: boolean;
}

function extractUserFromInvite(user: any): any {
	return user.profile;
}

function extractUserFromConnected(user: any): any {
	return user.gr_user || user.profile;
}

export default function ChatMain(props: OwnProps) {
	const rooms = useSelector((state: GreenroomState) => state.ChatReducer.rooms);
	const directMessages = useSelector((state: GreenroomState) => state.ChatReducer.directMessages);
	const selectedUser = useSelector((state: GreenroomState) => state.ChatReducer.selectedUser);
	const selectedRoom = useSelector((state: GreenroomState) => state.ChatReducer.selectedRoom);
	const shouldViewModerated = useSelector(
		(state: GreenroomState) => state.ChatReducer.shouldViewModerated,
	);
	const legacyModeratedComments = useSelector(
		(state: GreenroomState) => state.ChatReducer.legacyModeratedComments,
	);
	const moderatedEventsMessages = useSelector(
		(state: GreenroomState) => state.ChatReducer.moderatedEventsMessages,
	);
	const connectedUsers = useSelector(
		(state: GreenroomState) => state.SessionReducer.connectedUsers,
	);
	const user = useSelector((state: GreenroomState) => state.SessionReducer.user);
	const token = useSelector((state: GreenroomState) => state.SessionReducer.token);
	const meeting = useSelector((state: GreenroomState) => state.SessionReducer.meeting);
	const host = useSelector((state: GreenroomState) => state.SessionReducer.host);
	const invitations = useSelector((state: GreenroomState) => state.InvitationsReducer.invitations);
	const useModeratedQuestions = useSelector(
		(state: GreenroomState) => state.SettingsReducer.useModeratedQuestions,
	);
	const activeTab = useSelector((state: GreenroomState) => state.TabsReducer.activeTab);

	const dispatch = useDispatch();
	const { setChatNotificationsSeen } = useChat();
	const { socket, visible } = props;
	const [creatingRoom, setCreatingRoom] = useState<boolean>(false);
	const [creatingRoomClass, setCreatingRoomClass] = useState<string>('closed');
	const [newRoomName, setNewRoomName] = useState<string>('');
	const [newRoomParticipants, setNewRoomParticipants] = useState<ConnectedUser[]>([]);
	const mainSocket = useRef<Socket | null>(null);
	const modSocket = useRef<Socket | null>(null);
	const [inputText, setInputText] = useState('');

	const { loadLegacyModeratedChats } = useLoad();

	function handleElevated(chatMess: number) {
		if (token) {
			if (legacyModeratedComments[chatMess]) {
				dispatch(ChatActions.elevateComment(chatMess));
			} else {
				dispatch(ChatActions.getApprovedComment(token, chatMess));
			}
		}
	}

	function handleUnelevated(chatMess: number) {
		dispatch(ChatActions.unelevateComment(chatMess));
	}

	function handleDeleted(chatMess: number) {
		dispatch(ChatActions.deleteComment(chatMess));
	}

	function handleUndeleted(chatMess: number) {
		dispatch(ChatActions.undeleteComment(chatMess));
	}

	useEffect(() => {
		if (meeting!.brandlive_event && useModeratedQuestions && token) {
			dispatch(ChatActions.getModeratedComments(token, meeting!.brandlive_event));
		}
	}, [meeting, token, useModeratedQuestions]);

	const isProd = useMemo(() => window.location.hostname.includes('brandlive.com'), []);

	useEffect(() => {
		if (useModeratedQuestions) {
			// E3 Events Q&A - See `meeting-room.tsx`
			// Legacy Events Q&A
			// made a mistake in a PR, that should be ignored and we should hard connect to ion.brand.live
			if (meeting && meeting.brandlive_event) {
				const socketEndpoint = isProd ? 'ion.brand.live' : 'ion2.brand.live';
				mainSocket.current = new Socket(`${isProd ? 'bl' : 'staging-bl'}${meeting.brandlive_event}`, socketEndpoint);
				modSocket.current = new Socket(`${isProd ? 'blmod' : 'staging-blmod'}${meeting.brandlive_event}`, socketEndpoint);
				mainSocket.current.addListener(Socket.MESSAGE_TYPES.DELETE_MESSAGE, handleDeleted);
				mainSocket.current.addListener(Socket.MESSAGE_TYPES.UNDELETE_MESSAGE, handleUndeleted);
				modSocket.current.addListener('elevated', handleElevated);
				modSocket.current.addListener('unelevated', handleUnelevated);

				// Get legacy moderated chats from API when socket reconnected
				const socketReconnectFunction = () => {
					loadLegacyModeratedChats();
				};

				mainSocket.current.onReconnected = socketReconnectFunction;
				modSocket.current.onReconnected = socketReconnectFunction;
			}
		}

		return () => {
			mainSocket.current?.removeListener(Socket.MESSAGE_TYPES.DELETE_MESSAGE);
			mainSocket.current?.removeListener(Socket.MESSAGE_TYPES.UNDELETE_MESSAGE);
			modSocket.current?.removeListener('elevated');
			modSocket.current?.removeListener('unelevated');
			mainSocket.current?.disconnect();
			modSocket.current?.disconnect();
			mainSocket.current = null;
			modSocket.current = null;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [meeting?.brandlive_event, useModeratedQuestions]);

	useEffect(() => {
		if (token) {
			dispatch(ChatActions.loadChatPosts(token, meeting!.meeting));
			dispatch(ChatActions.loadRoomChat(token, meeting!.meeting));
			if (!host) {
				dispatch(InvitationActions.loadInvitations(token, meeting!.meeting));
			}
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [meeting, token]);

	const possibleUsers = useMemo(() => uniqueArray('gr_user', [
		...invitations.map(extractUserFromInvite).filter((profile: any) => !!profile.gr_user),
		...connectedUsers,
	]), [connectedUsers, invitations]);

	const possibleDMUsers = useMemo(() => {
		const dmUsers: any[] = [];
		const myEmail = user?.email;
		Object.keys(directMessages).forEach((key) => {
			if (myEmail && key?.includes(myEmail) && directMessages[key]?.messages?.[0]) {
				if (directMessages[key]?.messages?.[0]?.isOwnMessage) {
					dmUsers.push(directMessages[key]?.messages?.[0]?.recipient);
				} else {
					dmUsers.push(directMessages[key]?.messages?.[0]?.sender);
				}
			}
		});
		return uniqueArray('gr_user', [
			...possibleUsers,
			...dmUsers,
		]);
	}, [possibleUsers, directMessages]);

	function selectRoom({ room }: any) {
		dispatch(ChatActions.setSelectedRoom(room.id));
	}

	function selectConvo(convoProps: any) {
		const connUser = convoProps.user;
		const convo = directMessages[makeId([user!.email, connUser.email])];

		if (convo) {
			dispatch(ChatActions.setSelectedUser(connUser));
		} else {
			dispatch(ChatActions.createConvo(user!, connUser));
		}
	}

	function hideCreatingRoom() {
		setCreatingRoomClass('closed');
	}

	function createNewRoom() {
		if (!creatingRoom) {
			setCreatingRoom(true);
			setTimeout(() => setCreatingRoomClass('open'));
		} else {
			hideCreatingRoom();
		}
	}

	function onCreatingRoomHide() {
		if (creatingRoomClass === 'closed') { setCreatingRoom(false); }
	}

	function updateRoomName(e: React.ChangeEvent<HTMLInputElement>) {
		setNewRoomName(e.target.value);
	}

	function toggleNewRoomUser(connUser: ConnectedUser) {
		if (newRoomParticipants.some((_user: ConnectedUser) => _user.gr_user === connUser.gr_user)) {
			setNewRoomParticipants(newRoomParticipants.filter(
				(_user: ConnectedUser) => _user.gr_user !== connUser.gr_user,
			));
		} else {
			setNewRoomParticipants([...newRoomParticipants, connUser]);
		}
	}

	function submitRoom() {
		if (newRoomParticipants.length > 0 && newRoomName && token) {
			const profileIds = [...newRoomParticipants.map(extractUserFromConnected), user!.gr_user];

			// this was prev being handled in redux, but we need
			// the ID of the new room to connect this user to the room
			CreateRoom(token, meeting!.meeting, newRoomName, profileIds).then((room) => {
				dispatch(ChatActions.newRoom(room));

				connectedUsers.forEach((connUser: ConnectedUser) => {
					if (connUser.gr_user !== user!.gr_user && connUser.socketId) {
						socket?.newRoomCreated(connUser.socketId, room);
					}
				});
			});

			setNewRoomParticipants([]);
			setNewRoomName('');
			setCreatingRoomClass('closed');
		}
	}

	useEffect(() => {
		if (meeting?.meeting && socket && rooms) {
			socket.setRooms(rooms.map((room: ChatRoom) => `room.${meeting.meeting}.${room?.id}`));
		}
	}, [meeting, socket, rooms]);

	useEffect(() => {
		setChatNotificationsSeen();
	}, [activeTab]);

	function getBadge(room: ChatRoom): string {
		if (room.name === 'Presenter chat') {
			const possUsers = [...possibleUsers];
			const usersFromMessages = room?.messages.map((mess: ChatPost) => mess.sender) ?? [];
			const allParticipants = Utils.uniqueArray('email', [
				...usersFromMessages,
				...possUsers,
			]);

			return `${allParticipants.length} ${(allParticipants.length) !== 1 ? 'Members' : 'Member'}`;
		}
		const usersFromMessages = room?.messages.map((mess: ChatPost) => mess.sender?.gr_user) ?? [];
		const totalLength = new Set([...room.participants, ...usersFromMessages]).size;

		return `${totalLength} ${(totalLength) !== 1 ? 'Members' : 'Member'}`;
	}

	const newRoomParticipantsMemo = useMemo(() => (
		<div className="new-room-participants">
			{possibleUsers.map((u, i) => <InviteBadge
				key={u?.email ?? i}
				style={1}
				user={u}
				roomUsers={newRoomParticipants}
				callback={toggleNewRoomUser}/>)}
		</div>
	), [possibleUsers, newRoomParticipants]);

	const getLastModeratedCommentPreview = useMemo(() => {
		/*
		Need to reverse since `flex-direction: column-reverse`
		Other chat message arrays work with the above, so this
		seems like the lowest friction way of accomplishing
		the goal
		*/
		const moderatedEventsMessageArray = Array.from(
			moderatedEventsMessages,
			([, value]) => value,
		)
			.filter((message) => message.status !== EStatus.Deleted)
			.reverse();

		// we do not want to see deleted or unelevated messages
		const moderatedCommentArray = Object.values(
			legacyModeratedComments,
		).filter(
			(message: ChatMessage) => !message.deleted && message.elevated,
		);

		if (moderatedEventsMessageArray.length) {
			const lastMessage = moderatedEventsMessageArray[
				moderatedEventsMessageArray.length - 1
			];
			const { name, message } = Utils.getDisplayChatMessage(lastMessage, true);

			return {
				title: name,
				message,
			};
		} if (moderatedCommentArray.length) {
			const lastComment = moderatedCommentArray[moderatedCommentArray.length - 1];

			return {
				title: lastComment.name ?? 'Anonymous',
				message: Utils.stripHtml(lastComment.comment),
			};
		}

		return {};
	}, [legacyModeratedComments, moderatedEventsMessages]);

	const getUnseen = useCallback((
		messages: ChatPost[],
		meetingId?: number,
		roomId?: number,
		chatRooms?: ChatRoom[],
	): number => {
		if (meetingId && roomId !== undefined && rooms && user?.gr_user) {
			const currentChatRoomsStatus: {
				[key: number]: number
			} | undefined = Storage.getStorageObject(
				`chat-rooms-last-seen-${meetingId}-${user.gr_user}`,
			);
			const lastSeenMessageId = currentChatRoomsStatus?.[roomId];
			if (chatRooms) {
				const roomMessages = chatRooms.find((room) => room.id === roomId)?.messages;
				const roomMessagesLength = roomMessages?.length;

				if (lastSeenMessageId && roomMessagesLength) {
					return roomMessages.filter(
						(message) => !message.isOwnMessage && message.id > lastSeenMessageId,
					).length;
				}

				if (roomMessagesLength) {
					return roomMessagesLength;
				}
			}
		}

		return messages
			? messages.filter((message) => !message.seen && !message.isOwnMessage).length
			: 0;
	}, []);

	const getUnseenQAChat = useCallback(() => Array.from(
		moderatedEventsMessages,
		([, value]) => (value),
	).reduce((unseenMessageSum, message) => (
		!message.seen && message.status !== EStatus.Deleted
			? unseenMessageSum + 1
			: unseenMessageSum
	), 0), [moderatedEventsMessages]);

	// if selectedRoom or selectedUser has changed, reset the input text
	useEffect(() => {
		setInputText('');
	}, [selectedRoom, selectedUser]);

	const getChatContent = () => {
		if (!visible) {
			return <></>;
		}
		if (selectedRoom !== null) {
			return <ChatRoomView socket={socket} inputText={inputText} setInputText={setInputText} />;
		}
		if (selectedUser !== null) {
			return <ChatConvoView socket={socket} inputText={inputText} setInputText={setInputText} />;
		}
		if (shouldViewModerated) {
			return <ModeratedComments />;
		}

		return (
				<div className="chat-list">
					<div className="chat-group rooms-container margin-bottom-medium">
						<div className="tab-row justify-content-space-between">
							<label className='font-weight-bold'>Rooms</label>
							<button onClick={createNewRoom} className={classNames('create-room transparent', { open: creatingRoomClass === 'open' })}>
								{creatingRoomClass === 'open'
									? <Icon className='margin-right-small' name={ICONS.CLOSE} color={COLORS.PALE_GREEN} size={16}></Icon>
									: <Icon className='margin-right-small' name={ICONS.ADD} color={COLORS.PALE_GREEN} size={16}></Icon>
								}
								<span className='text-green'>Add a room</span>
							</button>
						</div>
						{creatingRoom && (
							<div className={classNames('create-room-wrapper', creatingRoomClass)} onTransitionEnd={onCreatingRoomHide}>
								<div className="dark-background" />
								<div onClick={hideCreatingRoom} className="click-background" />
								<div className="create-room-container">
									<div className="create-room-form">
										<div className="icon-container"><Icon name={ICONS.PEOPLE} size={22} color={COLORS.BLACK} ></Icon></div>
										<input type="text" placeholder={'Type a new room name...'} onChange={updateRoomName} value={newRoomName} />
									</div>
									{newRoomParticipantsMemo}
									<button disabled={newRoomParticipants.length === 0 || !newRoomName} onClick={submitRoom} className={classNames('create-chat-room', { ready: newRoomParticipants.length > 0 })}>
										<Icon name={ICONS.CHECK} size={18} color={COLORS.WHITE} ></Icon>
										<span>Create new room</span>
									</button>
								</div>
							</div>
						)}
						{rooms.map((room: ChatRoom, index: number) => (
							<div className='tab-row' key={index}>
								<ChatLink
									room={room}
									onClick={selectRoom}
									icon={(<Icon name={ICONS.PEOPLE} size={22} color={COLORS.BLACK} ></Icon>)}
									badge={getBadge(room)}
									title={room.name}
									unseen={getUnseen(room.messages, meeting?.meeting, room.id, rooms)}
									message={room.messages?.[0]}
									socket={socket}
								/>
							</div>
						))}
						{useModeratedQuestions && (
							<div className='tab-row'>
								<ChatLink
									room={{
										id: 0,
										name: 'Brandlive Q&A',
										meeting: 0,
										created_by: 0,
										participants: [],
										messages: [],
									}}
									icon={<Icon name={ICONS.QUESTION} size={18} color={COLORS.BLACK} />}
									title={'Brandlive Q&A'}
									unseen={getUnseenQAChat()}
									message={getLastModeratedCommentPreview}
									onClick={() => dispatch(ChatActions.viewModeratedComments(true))}
									socket={socket}
								/>
							</div>
						)}
					</div>

					{(possibleDMUsers.length > 1) && <div className="chat-group people-container">

						<div className="tab-row">
							<label className='font-weight-bold'>People</label>
						</div>
						{possibleDMUsers.map(
							(connUser: ConnectedUser, index: number) => (
								(connUser.gr_user && (connUser.gr_user !== user!.gr_user))
									? <div className='tab-row' key={index}>
										<ChatLink
											user={connUser}
											onClick={selectConvo}
											unseen={getUnseen(directMessages[
												makeId([connUser.email, user!.email])]?.messages)}
											message={directMessages[makeId([connUser.email, user!.email])]?.messages?.[0] || 'No Messages'}
											socket={socket}
										/>
									</div>
									: <React.Fragment key={index} ></React.Fragment>
							),
						)}
					</div>}
				</div>
		);
	};

	return (
		<div className={classNames(
			'chat-wrapper',
			'width-full',
			'height-full',
			{ 'position-absolute': ((selectedRoom !== null) || shouldViewModerated) || selectedUser !== null },
		)}>
			{getChatContent()}
		</div>
	);
}
